import { RefObject, useEffect, useState } from 'react';

interface UseGlassHeaderTitleRevealParams {
  scrollerRef: RefObject<HTMLDivElement | null>;
  headerRef: RefObject<HTMLDivElement | null>;
  detailsCardTestId: string;
  resetKey: string | number;
}

export const useGlassHeaderTitleReveal = ({
  scrollerRef,
  headerRef,
  detailsCardTestId,
  resetKey,
}: UseGlassHeaderTitleRevealParams): boolean => {
  const [isTitleRevealed, setIsTitleRevealed] = useState(false);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    // switching records swaps the card content under an unscrolled column; start clean
    scroller.scrollTop = 0;

    // reveal the glass-header title only once the SITE card's own header has scrolled
    // under the glass header — not as soon as the card above it (e.g. the partner card)
    // slides away, which is what a flat scrollTop threshold was mistakenly gating on
    const updateTitleReveal = () => {
      const card = scroller.querySelector<HTMLElement>(`[data-testid="${detailsCardTestId}"]`);
      const headerBottom = headerRef.current?.getBoundingClientRect().bottom ?? 0;
      setIsTitleRevealed(!!card && card.getBoundingClientRect().top <= headerBottom);
    };

    updateTitleReveal();

    scroller.addEventListener('scroll', updateTitleReveal, { passive: true });
    // the card mounts asynchronously (site details load after the panel does), so a
    // one-shot check at effect time can miss it — recheck whenever the content changes
    const contentObserver = new MutationObserver(updateTitleReveal);
    contentObserver.observe(scroller, { childList: true, subtree: true });

    return () => {
      scroller.removeEventListener('scroll', updateTitleReveal);
      contentObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  return isTitleRevealed;
};
